import { Button, Divider, FormControl, FormHelperText, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { LassoTextField } from '../../../components/LassoTextField';
import { useState } from 'react';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import { S3DestinationLocation, useAddDestinationLocationMutation } from '../../../api/dataProvidersApiSlice';
import { useNavigate } from 'react-router-dom';
import { ErrorOutlineOutlined } from '@mui/icons-material';
import LassoSelect from '../../../components/LassoSelect';

type Props = {
    setFormType: any;
}

const S3ConfigForm = ({ setFormType }: Props) => {
    const [addDestinationLocation] = useAddDestinationLocationMutation();
    const navigate = useNavigate();
    const [disableFields] = useState(false);

    const {
        handleSubmit,
        control,
        reset,
        setError,
        formState: { errors },
    } = useForm<S3DestinationLocation>({
        defaultValues: {
            name: '',
            type: 'S3MultiLevelDataProvider',
            clientIdentifier: '',
            clientToken: '',
            bucketName: '',
            startPath: '',
            endpoint: '',
            region: '',
        }
    })

    const handleChange = (event: SelectChangeEvent<unknown> , field: ControllerRenderProps<S3DestinationLocation, "type">) => {
        setFormType(event.target.value as string)
        field.onChange(event)
    }

    const onSubmit = (data: S3DestinationLocation) => {

        addDestinationLocation(data)
            .unwrap()
            .then(() => {
                reset();
                navigate('/file-download');
            })
            .catch((error) => {
                if (error.status === 400) {
                    Object.keys(error.data || {}).forEach((field: string) => {
                        if (field === 'name') {
                            setError('name', { type: 'Custom', message: error.data[field] })
                        }
                    })
                }
            });
    }
    return (
        <Stack direction="column" gap={4}>
            <Stack direction="column" gap="45px">
                <Stack sx={{ flexDirection: { xs: "column", md: "row" } }} gap={4}>
                    <Controller
                        name="name"
                        control={control}
                        rules={{
                            required: "This is required",
                            validate: {
                                noSpaces: v => !(v.indexOf(' ') >= 0) || "Name cannot have spaces"
                            }
                        }}
                        render={({ field }) =>
                            <LassoTextField
                                sx={{ flexGrow: 1 }}
                                label="Name"
                                error={'name' in errors}
                                helperText={errors?.name?.message || ""}
                                {...field}

                            />}
                    />
                    <Controller
                        name="type"
                        control={control}
                        render={({ field }) =>
                            <LassoSelect
                                sx={{ minWidth: "200px" }}
                                options={[{ label: "SSH", value: "UserkeyFlatDirSshDataProvider" }, { label: "S3 Bucket", value: "S3MultiLevelDataProvider" }]}
                                label="Destination Type"
                                {...field}
                                onChange={(e) => {handleChange(e, field)}}
                            />}
                    />
                </Stack>

                <Divider />
                    
                <Stack sx={{ flexDirection: { xs: "column", lg: "row" } }} gap={4}>
                    <Controller
                        name="clientIdentifier"
                        control={control}
                        rules={{
                            required: "This is required.",
                            minLength: {
                                value: 16,
                                message: "Length must be at least 16."
                            }
                        }}
                        render={({ field }) =>
                            <LassoTextField
                                label="Client Identifier"
                                fullWidth
                                disabled={disableFields}
                                placeholder="AKIAIOSFODNN7 EXAMPLE IDENTIFIER"
                                error={'clientIdentifier' in errors}
                                helperText={errors?.clientIdentifier?.message || ""}
                                {...field}
                            />}
                    />
                    <Controller
                        name="clientToken"
                        control={control}
                        rules={{
                            required: "This is required.",
                            minLength: {
                                value: 20,
                                message: "Length must be at least 20."
                            }
                        }}
                        render={({ field }) =>
                            <LassoTextField
                                label="Client Token"
                                fullWidth
                                disabled={disableFields}
                                placeholder="wJalrXUtnFEMIK7MDENGbPxRfiCY EXAMPLE TOKEN"
                                error={'clientToken' in errors}
                                helperText={errors?.clientToken?.message || ""}
                                {...field}
                            />}
                    />
                </Stack>
                <Typography fontSize={14} margin={0}>Definitions below are only needed for S3FlatDataProvider.</Typography>
                <Stack flexDirection="column" gap="45px">
                    <Stack direction="row" gap="40px" sx={{ flexDirection: { xs: "column", lg: "row" } }}>
                        <Controller
                            name="bucketName"
                            control={control}
                            rules={{ required: "This is required." }}
                            render={({ field }) =>
                                <FormControl fullWidth >
                                    <LassoTextField
                                        label="Bucket Name"
                                        fullWidth
                                        disabled={disableFields}
                                        placeholder="ABC-EXAMPLE-BUCKET1"
                                        error={'bucketName' in errors}
                                        helperText={errors?.bucketName?.message || ""}
                                        {...field}
                                    />
                                    {!('bucketName' in errors) && (
                                        <FormHelperText
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                position: "absolute",
                                                bottom: '0',
                                                transform: "translate(8px, 20px)",
                                                margin: 0,
                                                fontSize: "12px",
                                                gap: "3px"
                                            }}>
                                            <ErrorOutlineOutlined sx={{ width: "15px" }} />
                                        The bucket name should be unique to all of AWS
                                        </FormHelperText>
                                    )}
                                </FormControl>}
                        />
                        <Controller
                            name="startPath"
                            control={control}
                            render={({ field }) =>
                                <LassoTextField
                                    label="Client Starting Path"
                                    fullWidth
                                    disabled={disableFields}
                                    placeholder="path/to/client"
                                    {...field}
                                />}
                        />
                    </Stack>
                    <Stack direction="row" gap="40px" sx={{ flexDirection: { xs: "column", lg: "row" } }}>
                        <Controller
                            name="endpoint"
                            control={control}
                            render={({ field }) =>
                                <LassoTextField
                                    label="Endpoint"
                                    fullWidth
                                    disabled={disableFields}
                                    placeholder="/endpoint"
                                    {...field}
                                />}
                        />
                        <Controller
                            name="region"
                            control={control}
                            render={({ field }) =>
                                <LassoTextField
                                    label="Region"
                                    fullWidth
                                    disabled={disableFields}
                                    placeholder="us-east-1"
                                    {...field}
                                />}
                        />
                    </Stack>
                </Stack>
                <Button
                    disableRipple
                    variant="primary"
                    size="small"
                    sx={{ alignSelf: "center" }}
                    onClick={handleSubmit(onSubmit)}
                >
                    Add destination
                </Button>
            </Stack>
        </Stack>
    )
}

export default S3ConfigForm